import React, { useState, useEffect, useMemo } from 'react';
import { API_URL } from '../config/api';
import { adminFetch } from '../utils/adminFetch';
import './Stripe.css';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Pie } from 'react-chartjs-2';

ChartJS.register(ArcElement, Tooltip, Legend);

interface Transaction {
  id: string;
  amount: number;
  currency: string;
  status: string;
  customer: string;
  description: string;
  created: string;
  receiptUrl?: string;
  country?: string | null;
  handle?: string | null;
}

const Stripe: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [descriptionFilter, setDescriptionFilter] = useState<string>('Subscription creation');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 500;
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [selectedPreset, setSelectedPreset] = useState<string>('last30Days');

  // Helper function to format date to YYYY-MM-DD
  const formatDateToString = (date: Date): string => {
    return date.toISOString().split('T')[0];
  };

  // Date preset functions
  const applyDatePreset = (presetName: string) => {
    if (!presetName) return;

    const today = new Date();
    let start = new Date();
    let end = new Date();

    switch (presetName) {
      case 'last30Days':
        start.setDate(today.getDate() - 30);
        end = today;
        break;
      case 'thisMonth':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = today;
        break;
      case 'lastMonth':
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case 'yearToDate':
        start = new Date(today.getFullYear(), 0, 1);
        end = today;
        break;
      case 'allTime':
        start = new Date(2020, 0, 1); // Start from 2020
        end = today;
        break;
      default:
        // Handle month presets (0-11)
        const monthIndex = parseInt(presetName);
        if (!isNaN(monthIndex)) {
          start = new Date(today.getFullYear(), monthIndex, 1);
          end = new Date(today.getFullYear(), monthIndex + 1, 0);
        }
    }

    setStartDate(formatDateToString(start));
    setEndDate(formatDateToString(end));
    setSelectedPreset(presetName);
  };

  // Generate month options dynamically
  const getDatePresetOptions = () => {
    const today = new Date();
    const currentMonth = today.getMonth(); // 0-11
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

    const options = [
      { label: 'Last 30 Days', value: 'last30Days' },
      { label: 'This Month', value: 'thisMonth' },
      { label: 'Last Month', value: 'lastMonth' },
      { label: 'Year to Date', value: 'yearToDate' },
      { label: 'All Time', value: 'allTime' },
    ];

    // Add current year's months up to current month
    for (let i = 0; i <= currentMonth; i++) {
      options.push({ label: months[i], value: String(i) });
    }

    return options;
  };

  // Initialize dates on mount
  useEffect(() => {
    if (!startDate || !endDate) {
      applyDatePreset('last30Days');
    }
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      fetchTransactions();
      setCurrentPage(1); // Reset to first page when filter changes
    }
  }, [descriptionFilter, startDate, endDate]);

  const fetchTransactions = async () => {
    try {
      setLoading(true);

      const url = `${API_URL}/api/stripe/transactions?startDate=${new Date(startDate).toISOString()}&endDate=${new Date(endDate).toISOString()}&descriptionFilter=${encodeURIComponent(descriptionFilter)}`;

      const response = await adminFetch(url);

      if (response.ok) {
        const data = await response.json();
        setTransactions(data.data || []);
      } else {
        console.error('Failed to fetch transactions:', response.status);
      }
    } catch (error) {
      console.error('Failed to fetch Stripe transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCountryDisplay = (countryCode: string | null | undefined) => {
    if (!countryCode) return '-';

    const flag = countryCode
      .toUpperCase()
      .split('')
      .map(char => 127397 + char.charCodeAt(0));
    const flagEmoji = String.fromCodePoint(...flag);

    // Country code to name mapping
    const countryNames: { [key: string]: string } = {
      'US': 'United States',
      'GB': 'United Kingdom',
      'CA': 'Canada',
      'AU': 'Australia',
      'DE': 'Germany',
      'FR': 'France',
      'IT': 'Italy',
      'ES': 'Spain',
      'NL': 'Netherlands',
      'BE': 'Belgium',
      'CH': 'Switzerland',
      'AT': 'Austria',
      'SE': 'Sweden',
      'NO': 'Norway',
      'DK': 'Denmark',
      'FI': 'Finland',
      'PL': 'Poland',
      'IE': 'Ireland',
      'PT': 'Portugal',
      'GR': 'Greece',
      'JP': 'Japan',
      'KR': 'South Korea',
      'CN': 'China',
      'IN': 'India',
      'SG': 'Singapore',
      'HK': 'Hong Kong',
      'TW': 'Taiwan',
      'BR': 'Brazil',
      'MX': 'Mexico',
      'AR': 'Argentina',
      'NZ': 'New Zealand',
      'ZA': 'South Africa',
      'IS': 'Iceland',
      'IL': 'Israel',
      'RU': 'Russia',
      'UA': 'Ukraine',
      'TR': 'Turkey',
      'SA': 'Saudi Arabia',
      'AE': 'United Arab Emirates',
      'EG': 'Egypt',
      'NG': 'Nigeria',
      'KE': 'Kenya',
      'TH': 'Thailand',
      'VN': 'Vietnam',
      'ID': 'Indonesia',
      'MY': 'Malaysia',
      'PH': 'Philippines',
      'CZ': 'Czech Republic',
      'HU': 'Hungary',
      'RO': 'Romania',
      'BG': 'Bulgaria',
      'HR': 'Croatia',
      'SK': 'Slovakia',
      'SI': 'Slovenia',
      'LT': 'Lithuania',
      'LV': 'Latvia',
      'EE': 'Estonia',
      'CL': 'Chile',
      'CO': 'Colombia',
      'PE': 'Peru',
      'VE': 'Venezuela'
    };

    const countryName = countryNames[countryCode.toUpperCase()] || countryCode.toUpperCase();

    return (
      <span className="country-display">
        <span className="country-flag">{flagEmoji}</span>
        <span className="country-name">{countryName}</span>
      </span>
    );
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getAmountClass = (amount: number) => {
    if (amount >= 50) return 'amount-high';
    if (amount >= 20) return 'amount-medium';
    return 'amount-low';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Calculate revenue by country
  const revenueByCountry = useMemo(() => {
    const countryRevenue: { [key: string]: number } = {};

    transactions.forEach(tx => {
      const country = tx.country || 'Unknown';
      countryRevenue[country] = (countryRevenue[country] || 0) + tx.amount;
    });

    return Object.entries(countryRevenue)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10); // Top 10 countries
  }, [transactions]);

  // Calculate revenue by handle (exclude Unknown and unavailable)
  const revenueByHandle = useMemo(() => {
    const handleRevenue: { [key: string]: number } = {};

    transactions.forEach(tx => {
      const handle = tx.handle;
      if (handle && handle !== 'Unknown' && handle !== 'unavailable') {
        handleRevenue[handle] = (handleRevenue[handle] || 0) + tx.amount;
      }
    });

    return Object.entries(handleRevenue)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10); // Top 10 handles
  }, [transactions]);

  // Country chart data
  const countryChartData = {
    labels: revenueByCountry.map(([country, revenue]) => {
      let countryName = country;
      if (country !== 'Unknown') {
        const countryNames: { [key: string]: string } = {
          'US': 'United States', 'GB': 'United Kingdom', 'CA': 'Canada',
          'AU': 'Australia', 'DE': 'Germany', 'FR': 'France', 'IT': 'Italy',
          'ES': 'Spain', 'NL': 'Netherlands', 'BE': 'Belgium', 'CH': 'Switzerland',
          'AT': 'Austria', 'SE': 'Sweden', 'NO': 'Norway', 'DK': 'Denmark',
          'FI': 'Finland', 'PL': 'Poland', 'IE': 'Ireland', 'PT': 'Portugal',
          'GR': 'Greece', 'JP': 'Japan', 'KR': 'South Korea', 'CN': 'China',
          'IN': 'India', 'SG': 'Singapore', 'HK': 'Hong Kong', 'TW': 'Taiwan',
          'BR': 'Brazil', 'MX': 'Mexico', 'AR': 'Argentina', 'NZ': 'New Zealand',
          'ZA': 'South Africa', 'IS': 'Iceland', 'IL': 'Israel', 'RU': 'Russia',
          'UA': 'Ukraine', 'TR': 'Turkey', 'SA': 'Saudi Arabia',
          'AE': 'United Arab Emirates', 'EG': 'Egypt', 'NG': 'Nigeria',
          'KE': 'Kenya', 'TH': 'Thailand', 'VN': 'Vietnam', 'ID': 'Indonesia',
          'MY': 'Malaysia', 'PH': 'Philippines', 'CZ': 'Czech Republic',
          'HU': 'Hungary', 'RO': 'Romania', 'BG': 'Bulgaria', 'HR': 'Croatia',
          'SK': 'Slovakia', 'SI': 'Slovenia', 'LT': 'Lithuania', 'LV': 'Latvia',
          'EE': 'Estonia', 'CL': 'Chile', 'CO': 'Colombia', 'PE': 'Peru',
          'VE': 'Venezuela'
        };
        countryName = countryNames[country.toUpperCase()] || country.toUpperCase();
      }
      return `${countryName} ($${revenue.toFixed(2)})`;
    }),
    datasets: [{
      data: revenueByCountry.map(([, revenue]) => revenue),
      backgroundColor: [
        '#635bff', '#0074d4', '#00d924', '#ffa94d', '#ff6b6b',
        '#845ef7', '#20c997', '#fab005', '#ff8787', '#51cf66'
      ],
      borderWidth: 2,
      borderColor: '#fff'
    }]
  };

  // Handle chart data
  const handleChartData = {
    labels: revenueByHandle.map(([handle, revenue]) => `${handle} ($${revenue.toFixed(2)})`),
    datasets: [{
      data: revenueByHandle.map(([, revenue]) => revenue),
      backgroundColor: [
        '#4c6ef5', '#be4bdb', '#f06595', '#ff6b6b', '#ff922b',
        '#fab005', '#82c91e', '#40c057', '#12b886', '#15aabf'
      ],
      borderWidth: 2,
      borderColor: '#fff'
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right' as const,
        labels: {
          boxWidth: 15,
          padding: 10,
          font: {
            size: 11
          }
        }
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const value = context.parsed || 0;
            return `$${value.toFixed(2)}`;
          }
        }
      }
    }
  };

  // Pagination logic
  const totalPages = Math.ceil(transactions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedTransactions = transactions.slice(startIndex, endIndex);

  const goToPage = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  if (loading) {
    return (
      <div className="stripe-container">
        <div className="loading">Loading transactions...</div>
      </div>
    );
  }

  return (
    <div className="stripe-container">
      <div className="stripe-header">
        <h1>💳 Stripe Transactions</h1>

        <div className="filter-controls" style={{ display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label htmlFor="date-preset">Date Range:</label>
            <select
              id="date-preset"
              value={selectedPreset}
              onChange={(e) => applyDatePreset(e.target.value)}
              className="filter-select"
              style={{ minWidth: '150px' }}
            >
              {getDatePresetOptions().map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label>Custom Range:</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setSelectedPreset('');
                }}
                className="filter-select"
                style={{ width: '150px' }}
              />
              <span>to</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setSelectedPreset('');
                }}
                className="filter-select"
                style={{ width: '150px' }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label htmlFor="description-filter">Type:</label>
            <select
              id="description-filter"
              value={descriptionFilter}
              onChange={(e) => setDescriptionFilter(e.target.value)}
              className="filter-select"
              style={{ minWidth: '180px' }}
            >
              <option value="Subscription creation">Subscription creation</option>
              <option value="Subscription update">Subscription update</option>
              <option value="All">All</option>
            </select>
          </div>
        </div>
      </div>

      <div className="stripe-content">
        {/* Charts Section */}
        <div className="charts-section">
          <div className="chart-card">
            <h2>🌍 Revenue by Country (Top 10)</h2>
            <div className="chart-wrapper">
              <Pie data={countryChartData} options={chartOptions} />
            </div>
          </div>
          <div className="chart-card">
            <h2>👤 Revenue by Handle (Top 10)</h2>
            <div className="chart-wrapper">
              <Pie data={handleChartData} options={chartOptions} />
            </div>
          </div>
        </div>

        {/* Transactions Table */}
        <section className="stripe-section">
          <div className="table-header">
            <h2>📊 Transactions ({transactions.length} total)</h2>
            {totalPages > 1 && (
              <div className="pagination-info">
                Showing {startIndex + 1}-{Math.min(endIndex, transactions.length)} of {transactions.length}
              </div>
            )}
          </div>

          <div className="transactions-table">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Description</th>
                  <th>Customer</th>
                  <th>Handle</th>
                  <th>Country</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {paginatedTransactions.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                      No transactions found
                    </td>
                  </tr>
                ) : (
                  paginatedTransactions.map((transaction) => (
                    <tr key={transaction.id}>
                      <td>{formatDate(transaction.created)}</td>
                      <td>{transaction.description}</td>
                      <td>{transaction.customer}</td>
                      <td className="handle-cell">{transaction.handle || '-'}</td>
                      <td className="country-cell">{getCountryDisplay(transaction.country)}</td>
                      <td className={`amount-cell ${getAmountClass(transaction.amount)}`}>
                        {formatCurrency(transaction.amount)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => goToPage(1)}
                disabled={currentPage === 1}
                className="pagination-btn"
              >
                ««
              </button>
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="pagination-btn"
              >
                «
              </button>

              <span className="pagination-pages">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => goToPage(pageNum)}
                      className={`pagination-btn ${currentPage === pageNum ? 'active' : ''}`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </span>

              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="pagination-btn"
              >
                »
              </button>
              <button
                onClick={() => goToPage(totalPages)}
                disabled={currentPage === totalPages}
                className="pagination-btn"
              >
                »»
              </button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default Stripe;
